import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EnvironmentService } from 'projects/shared/services/environment.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PortalScope } from '../login/login.component';
import { AuthenticationRequest } from '../models/auth/auth-request.model';
import { AuthenticationResponse, AuthenticationUserCompany } from '../models/auth/auth-response.model';
import { ResetPasswordRequest } from '../models/auth/reset-password-request.model';
import { Constants } from './constants';
import { DataService } from './data.service';
import { UtilsService } from './utils.service';
import { CompanyInfo } from '../models/company-info.model';
import { PasswordPolicyResponse } from '../models/auth/password-policy-response.model';
import { PENDING_ACK_KEY } from 'projects/borrower-app/src/app/services/spanish-acknowledgment';

const headers = {
  'Content-Type': 'application/json',
  'auth-req': 'true',
  'X-Requested-With': 'XMLHttpRequest',
  'APPLICATION_NAME': 'OnlineAppV3',
  'APPLICATION_VERSION': '3.0' // Flow guid will go to the version
};

@Injectable()
export class AuthService {

  private readonly _authApiBaseUrl: string;

  constructor(
    private readonly _http: HttpClient,
    private readonly _dataService: DataService,
    private readonly _environmentService: EnvironmentService,
    private readonly _utilsService: UtilsService) {
    this._authApiBaseUrl = this._environmentService.apiInfo.authApiBaseUrl;
  }

  confirmRegistrationToken = (userName: string, token: string, companyGuid: string): Observable<any> => {
    const payload = {
      username: userName,
      token: token,
      companyGuid: companyGuid
    }
    const portal = this._environmentService.scope === PortalScope.Agent ? "AgentPortal" : "BorrowerPortal";
    return this._dataService.postWithoutAuth(`api/${portal}/ConfirmRegistrationToken`, payload);
  }

  confirmRegistration = (userName: string, token: string, password: string, confirmPassword: string, companyGuid: string) => {
    const portal = this._environmentService.scope === PortalScope.Agent ? "AgentPortal" : "BorrowerPortal";
    const payload = { userName: userName, token: token, password: password, confirmPassword: confirmPassword, isConfirmed: true, companyGuid: companyGuid };
    return this._dataService.postWithoutAuth(`api/${portal}/ConfirmRegistration`, payload);
  }

  isLoggedIn = (): boolean => {
    const authData = localStorage.getItem(Constants.authToken);
    return authData !== null;
  }

  logout = () => {
    localStorage.removeItem(Constants.authorization.authorizationDataKey);
    // LLS-886 §5: a signed-out Spanish acknowledgment must not carry over to whoever signs in next on this tab.
    try { sessionStorage.removeItem(PENDING_ACK_KEY); } catch { /* ignore */ }
  }

  setAdminToken = (token: string) => {
    localStorage.setItem(Constants.authorization.authorizationDataKey, JSON.stringify({
      token: token,
      useRefreshTokens: true,
      scope: 'Admin',
      expiresAt: new Date(4000, 2, 2)
    }));
  }

  setBorrowerToken = (token: string) => {
    localStorage.setItem(Constants.authorization.authorizationDataKey, JSON.stringify({
      token: token,
      useRefreshTokens: true,
      scope: 'Borrower',
      expiresAt: new Date(4000, 2, 2)
    }));
  }

  postSsoCompleted = (samlRequestId: string, relayState: string, assertionConsumerServiceUrl: string, jwt: string): Observable<string> => {
    const url = this._authApiBaseUrl + `/api/auth/saml/borrower-sso/complete`;
    return this._dataService.postWithExternalToken(
      url,
      {
        samlRequestId: samlRequestId,
        relayState: relayState,
        assertionConsumerServiceUrl: assertionConsumerServiceUrl
      }, jwt, false, { observe: 'response', responseType: 'text'}).pipe(map((response) => {
        return response.body;
      })
    )};

  loginWithToken(authToken: string, companyGuid: string, userCompanyGuid: string): Observable<AuthenticationResponse> {
    localStorage.setItem(Constants.authorization.authorizationDataKey, JSON.stringify({
      token: authToken
    }));
    return this.exchangeToken(companyGuid, userCompanyGuid);
  }

  sendTwoFactorEmailCode = (userCompanyGuid: string) => {
    const url = this._authApiBaseUrl + `/api/auth/SendTwoFactorEmailCode?userCompanyGuid=${userCompanyGuid}`;
    return this._dataService.postWithoutAuth(url, {}, false);
  }

  sendTwoFactorPhoneCode = (userCompanyGuid: string) => {
    const url = this._authApiBaseUrl + + `/api/auth/SendTwoFactorPhoneCode?userCompanyGuid=${userCompanyGuid}`;
    return this._dataService.postWithoutAuth(url, {}, false);
  }

  sentPasswordResetLink = (email: string, scope: string, userCompanyGuid: string) => {
    const url = this._authApiBaseUrl + `/api/auth/ForgotPassword`;
    const payload = {
      email: email,
      scope: scope || "Borrower",
      userCompanyGuid: userCompanyGuid
    }
    return this._dataService.postWithoutAuth(url, payload, false);
  }

  resetPassword = (resetPasswordRequest: ResetPasswordRequest) => {
    const url = this._authApiBaseUrl + `/api/auth/ResetPassword`;
    return this._dataService.postWithoutAuth(url, resetPasswordRequest, false);
  }

  resetPasswordWithRecurityCode = (resetPasswordRequest: ResetPasswordRequest) => {
    const url = this._authApiBaseUrl + `/api/auth/ResetPassword/code`;
    return this._dataService.postWithoutAuth(url, resetPasswordRequest, false);
  }

  refreshToken = (refreshToken: string): Observable<AuthenticationResponse> => {
    const url = this._authApiBaseUrl + `/api/auth/refreshToken?refreshToken=${encodeURIComponent(refreshToken)}`;
    return this._dataService.postWithoutAuth(url, {}, false).pipe(map(response => {
      // A failed refresh comes back as a 200 carrying an error body; storing it would replace the still
      // usable credentials with undefined and force the user to log in again on the next refresh.
      if (response?.jwtToken && response?.refreshToken) {
        this.saveAuthResponseInLocalStorage(response);
      }
      return response;
    }));
  }

  getDefaultOnlineAppUrl = (companyGuid?: string): Observable<any> => {
    const baseUrl = this._environmentService.apiInfo.publicApiBaseUrl;
    let url = `${baseUrl}/api/publicapi/GetDefaultOnlineAppUrl`;
    if (companyGuid) {
      url += '/' + companyGuid;
    }
    return this._http.get(url, { responseType: 'text' });
  }

  exchangeToken = (companyGuid: string, userCompanyGuid: string): Observable<AuthenticationResponse> => {
    const url = this._authApiBaseUrl + `/api/auth/exchangeToken?companyGuid=${companyGuid}&userType=Borrower&userCompanyGuid=${userCompanyGuid}`;
    return this._dataService.post(url, {}, false, null, false);
  }

  signIn = (request: AuthenticationRequest): Observable<AuthenticationResponse> => {
    const url = this._authApiBaseUrl + `/api/auth/signin`;
    return this._dataService.postWithoutAuth(url, request, false).pipe(map(response => {
      if (response.loginStatus === 'Success') {
        this.saveAuthResponseInLocalStorage(response);
      }
      return response;
    }));
  }

  challenge = (companyGuid: string, scope: string, returnUrl: string): Observable<any> => {
    const url = this._authApiBaseUrl + `/api/auth/connect/challenge/${companyGuid}/${scope}?returnUrl=${encodeURIComponent(returnUrl)}`;
    return this._dataService.postWithoutAuth(url, {}, false);
  }

  getAvailableCompanies = (email: string, scope: string): Observable<AuthenticationUserCompany[]> => {
    const url = this._authApiBaseUrl + `/api/auth/AvailableCompanies?userName=${encodeURIComponent(email)}&scope=${scope}`;
    return this._dataService.get(url, null, null, false);
  }

  confirmUpdatePhoneAnonymous = (phoneNumber: string, areacode: string, code: string, request: AuthenticationRequest): Observable<any> => {
    const url = this._authApiBaseUrl + `/api/auth/ConfirmUpdatePhoneAnonymous?phone=${phoneNumber}&areacode=${areacode}&code=${code}`;
    return this._dataService.postWithoutAuth(url, request, false);
  }

  updatePhoneAnonymous = (phoneNumber: string, areacode: string, request: AuthenticationRequest): Observable<any> => {
    const url = this._authApiBaseUrl + `/api/auth/UpdatePhoneAnonymous?phone=${phoneNumber}&areacode=${areacode}`;
    return this._dataService.postWithoutAuth(url, request, false);
  }

  getCompanyInfo = (userScope: PortalScope): Observable<CompanyInfo> => {
    const url = this._authApiBaseUrl + `/api/auth/company-info?scope=${userScope}`;
    return this._dataService.get(url, null, null, false);
  }

  saveAuthTokenDirectly = (authToken: string, refreshToken: string) => {
    const jwtPayload = this._utilsService.parseJwt(authToken);

    localStorage.setItem(Constants.authorization.authorizationDataKey, JSON.stringify({
      token: authToken,
      userName: jwtPayload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
      userId: jwtPayload.sub,
      refreshToken: refreshToken,
      useRefreshTokens: true,
      scope: jwtPayload.AspNetRole,
      companyGuid: jwtPayload.CompanyGuid,
      expiresAt: this._utilsService.getTokenExpireDate(jwtPayload.exp)
    }));
  }

  getPasswordPolicy = (userCompanyGuid: string): Observable<PasswordPolicyResponse> => {
    const url = this._authApiBaseUrl + `/api/auth/password-policy?userCompanyGuid=${encodeURIComponent(userCompanyGuid)}`;
    return this._dataService.getWithoutAuth(url, false);
  }

  getPasswordPolicyByCompanyGuid = (companyGuid: string): Observable<PasswordPolicyResponse> => {
    const url = this._authApiBaseUrl + `/api/auth/password-policy?companyGuid=${encodeURIComponent(companyGuid)}`;
    return this._dataService.getWithoutAuth(url, false);
  }

  private saveAuthResponseInLocalStorage = (authResponse: AuthenticationResponse) => {
    const jwtPayload = this._utilsService.parseJwt(authResponse.jwtToken);

    localStorage.setItem(Constants.authorization.authorizationDataKey, JSON.stringify({
      token: authResponse.jwtToken,
      userName: authResponse.userName,
      userId: authResponse.userCompanyGuid,
      refreshToken: authResponse.refreshToken,
      useRefreshTokens: true,
      scope: authResponse.userType,
      companyGuid: jwtPayload.CompanyGuid,
      expiresAt: this._utilsService.getTokenExpireDate(jwtPayload.exp)
    }));
  }
}
